import React, { Fragment, useEffect, useState } from "react";
import moment from "moment";
import { any, func } from "prop-types";
import { connect } from "react-redux";
import FormCreator from "../common/FormCreator";
import history from "../history";
import HorizontalLine from "../common/HorizontalLine";
import { postNHSReferenceForm } from "../redux/actions";
import OptionsButton from "../common/OptionsButton/index";

const NHSReferences = ({ appData, postNHSReferenceFormFunc }) => {
  let initialWorkReference = [
    {
      from: "",
      to: "",
      referee: "",
      jobTitle: "",
      company: "",
      email: "",
      telephone: "",
      canContactReferee: "",
      type: "workReference",
      additionalInfo: "",
    },
  ];

  let initialPersonalReference = [
    {
      from: "",
      to: "",
      referee: "",
      email: "",
      telephone: "",
      canContactReferee: "",
      type: "personalReference",
    },
    {
      from: "",
      to: "",
      referee: "",
      email: "",
      telephone: "",
      canContactReferee: "",
      type: "personalReference",
    },
  ];

  const initialErrorMessages = {
    work: {
      toAfterFromDate: "",
      wrongDate: "",
      tooBigGap: "",
    },
    personal: {
      toAfterFromDate: "",
      wrongDate: "",
      tooBigGap: "",
      yearsRange: "",
    },
  };

  if (appData.nhsReferenceForm && appData.nhsReferenceForm.workReferenceData) {
    initialWorkReference = Object.keys(
      appData.nhsReferenceForm.workReferenceData
    ).map((key) => appData.nhsReferenceForm.workReferenceData[key]);
  }

  if (
    appData.nhsReferenceForm &&
    appData.nhsReferenceForm.personalReferenceData
  ) {
    initialPersonalReference = Object.keys(
      appData.nhsReferenceForm.personalReferenceData
    ).map((key) => appData.nhsReferenceForm.personalReferenceData[key]);
  }

  const [shouldDisable, setShouldDisable] = useState(true);
  const [workReferenceData, setWorkReferenceData] =
    useState(initialWorkReference);
  const [personalReferenceData, setPersonalReferenceData] = useState(
    initialPersonalReference
  );
  const [validationErrorMessage, setValidationErrorMessage] =
    useState(initialErrorMessages);
  const [threeYearsErrorMessage, setThreeYearsErrorMessage] = useState("");

  /**
   * Validates form against no empty values
   *
   * @param data form fields
   */
  const checkForNoEmptyValues = (data) => {
    let isNoEmptyValues = true;
    data.forEach((el) => {
      Object.keys(el).forEach((key) => {
        if (!el[key] && key !== "additionalInfo") {
          isNoEmptyValues = false;
        }
      });
    });
    return isNoEmptyValues;
  };

  /**
   * Validation difference in 3 years range between referee information
   *
   * @param oldest  'to' input field
   * @param earliest   'from' input field
   */
  const validateThreeYearsRange = (oldest, earliest) => {
    if (oldest && earliest) {
      const r = moment(earliest).diff(moment(oldest), "years", true);
      if (r < 3) {
        setThreeYearsErrorMessage(
          "Please ensure you add another work reference, so you cover 3 years worth of referencing."
        );
      } else setThreeYearsErrorMessage("");
    }
  };

  /**
   * Validation difference in 5 years range between referee information
   *
   * @param messages current form error messages
   * @param data form fields
   */
  const validateFiveYearsRange = (messages, data) => {
    const numberOfYears = 5;
    const validateFiveYearsRangeMessages = [...messages];
    for (let i = 0; i < data.length; i += 1) {
      if (data[i].from && data[i].to) {
        const start = moment(data[i].from, "YYYY/MM/DD");
        const end = moment(data[i].to, "YYYY/MM/DD");
        const r = moment(end).diff(moment(start), "years", true);
        let messageText = "";
        if (r < numberOfYears) {
          messageText = `Please ensure the personal reference you have entered covers the last ${numberOfYears} years.`;
        }

        const prev = validateFiveYearsRangeMessages[i]
          ? validateFiveYearsRangeMessages[i]
          : {};
        validateFiveYearsRangeMessages[i] = {
          ...prev,
          yearsRange: messageText,
        };
      }
    }
    return validateFiveYearsRangeMessages;
  };

  /**
   * Validation difference in days between referee information
   *
   * @param messages current form error messages
   * @param data form fields
   * @param numberOfDays maximum number of days required
   */
  const validateDaysRange = (messages, data, numberOfDays) => {
    const validateDaysRangeMessages = [...messages];
    for (let i = 0; i < data.length - 1; i += 1) {
      if (data[i + 1].from && data[i + 1].to && data[i].from && data[i].to) {
        const start = moment(data[i].to, "YYYY/MM/DD");
        const end = moment(data[i + 1].from, "YYYY/MM/DD");
        const r = moment(end).diff(moment(start), "days", true);
        if (r > numberOfDays) {
          let messageText = "";
          if (data[i].type === "personalReference") {
            messageText = `You have a gap of more than ${numberOfDays} days within your reference history,
            please amend as we can not accept a gap of more than ${numberOfDays} days without these details.`;
          }
          if (
            data[i].type === "workReference" &&
            data[i + 1].additionalInfo === ""
          ) {
            messageText = `You have a gap of more than ${numberOfDays} days within your reference history,
            please amend or provide additional information below about the reason for the gap, as we cannot accept a gap of more than ${numberOfDays} days without these details.`;
          }
          const prev = validateDaysRangeMessages[i + 1]
            ? validateDaysRangeMessages[i + 1]
            : {};
          validateDaysRangeMessages[i + 1] = {
            ...prev,
            tooBigGap: messageText,
          };
        }
      }
    }
    return validateDaysRangeMessages;
  };

  /**
   * Validation if 'from' date is before 'to' date
   * @param messages  current existing array of validation messages to populate
   * @param data   form fields
   * @param oldest  'to' input field
   * @param earliest 'from' input field
   */
  const validateBetweenFromAndTo = (messages, data, oldest, earliest) => {
    const betweenFromAndToMessages = [...messages];
    let oldestDate = oldest;
    let earliestDate = earliest;
    data.forEach((el, i) => {
      const from = el.from ? moment(el.from).format("YYYY-MM-DD") : null;
      const to = el.to ? moment(el.to).format("YYYY-MM-DD") : null;
      if (from && to && moment(to).isBefore(from)) {
        const prev = betweenFromAndToMessages[i]
          ? betweenFromAndToMessages[i]
          : {};
        betweenFromAndToMessages[i] = {
          ...prev,
          toAfterFromDate: "From date can not be after to date",
        };
      }
      if (from && moment(from).isBefore(oldestDate)) {
        oldestDate = from;
      }
      if (to && moment(to).isAfter(earliestDate)) {
        earliestDate = to;
      }
    });
    return { betweenFromAndToMessages, oldestDate, earliestDate };
  };

  /**
   * Validation if 'to' date is after current date
   * @param messages  current existing array of validation messages to populate
   * @param data   form fields
   */
  const validateDate = (messages, data) => {
    const validateDateMessages = [...messages];
    const currentDate = moment().format("YYYY-MM-DD");
    data.forEach((el, i) => {
      const to = el.to ? moment(el.to).format("YYYY-MM-DD") : null;
      if (to && moment(to).isAfter(currentDate)) {
        const prev = validateDateMessages[i] ? validateDateMessages[i] : {};
        validateDateMessages[i] = {
          ...prev,
          wrongDate: "To date can not be after current date.",
        };
      }
    });

    return validateDateMessages;
  };

  /**
   * Actual form validation.
   * Validates the form against the 'to' date can not be earlier then 'form' date
   * Validate the form against the given range of the years or days should be equal or more than required
   *
   * @param data  form fields
   * @param numberOfYears minimum number of years required
   * @param numberOfDays minimum number of days required
   */
  const validation = (data, numberOfYears, numberOfDays) => {
    let validationMessages = data.map(() => ({}));
    if (data[0].to && data[0].from) {
      const oldDate = moment(data[0].to).format("YYYY-MM-DD");
      const earlyDate = moment(data[0].from).format("YYYY-MM-DD");
      const { betweenFromAndToMessages, oldestDate, earliestDate } =
        validateBetweenFromAndTo(validationMessages, data, oldDate, earlyDate);
      const validateDateMessages = validateDate(betweenFromAndToMessages, data);
      const validateDaysRangeMessages = validateDaysRange(
        validateDateMessages,
        data,
        numberOfDays
      );
      if (numberOfYears === 3)
        validateThreeYearsRange(oldestDate, earliestDate);
      let finalValidatedMessages = [...validateDaysRangeMessages];
      if (numberOfYears === 5) {
        finalValidatedMessages = validateFiveYearsRange(
          validateDaysRangeMessages,
          data
        );
      }
      validationMessages = [...finalValidatedMessages];
    }
    return validationMessages;
  };

  /**
   * Support function for the form save button
   * Return true if no messages in validation messages
   * Return false if messages exists in validation messages
   * Bypass the validation of tooBigGap field for the work reference form
   *
   * @param validationMessages
   */
  const checkValidation = (validationMessages) => {
    let areErrorMessagesEmpty = true;
    validationMessages.forEach((el) => {
      Object.keys(el).forEach((key) => {
        if (el[key] && Object.keys(el[key]).length > 0) {
          areErrorMessagesEmpty = false;
        }
      });
    });
    return areErrorMessagesEmpty;
  };

  useEffect(() => {
    const areAllWorkFieldsFilled = checkForNoEmptyValues(workReferenceData);
    const areAllPersonalFieldsFilled = checkForNoEmptyValues(
      personalReferenceData
    );
    const workRefValidationMessages = validation(workReferenceData, 3, 28);
    const personalRefValidationMessages = validation(
      personalReferenceData,
      5,
      28
    );
    const areWorkMessagesEmpty = checkValidation(workRefValidationMessages);
    const arePersonalMessagesEmpty = checkValidation(
      personalRefValidationMessages
    );

    const validationMessages = {
      work: {
        ...workRefValidationMessages,
      },
      personal: {
        ...personalRefValidationMessages,
      },
    };

    setValidationErrorMessage(validationMessages);

    setShouldDisable(
      !(
        areAllWorkFieldsFilled &&
        areAllPersonalFieldsFilled &&
        areWorkMessagesEmpty &&
        arePersonalMessagesEmpty &&
        threeYearsErrorMessage === ""
      )
    );
  }, [
    shouldDisable,
    workReferenceData,
    personalReferenceData,
    threeYearsErrorMessage,
  ]);

  /**
   * Saves the completed form
   */
  const handleSave = () => {
    const { candidateRef, emailAddress } = appData;
    const dataToSubmit = {
      candidateRef,
      emailAddress,
      nhsReferences: { workReferenceData, personalReferenceData },
    };
    postNHSReferenceFormFunc(dataToSubmit);
    history.push("/profile");
  };
  /**
   * Sets value for the input and updates data for the given form
   *
   * @param data  form fields
   * @param value input value
   * @param key input key
   * @param i input index
   */
  const setValue = (data, value, key, i) => {
    const mappedHistoryData = data.map((el, index) => {
      if (i === index) {
        return { ...el, [key]: value };
      }
      return el;
    });
    if (JSON.stringify(data) === JSON.stringify(workReferenceData)) {
      setWorkReferenceData(mappedHistoryData);
    }
    if (JSON.stringify(data) === JSON.stringify(personalReferenceData)) {
      setPersonalReferenceData(mappedHistoryData);
    }
  };

  /**
   * Removes an additional work reference form the list of work reference forms
   */
  const removeLast = () => {
    const newArr = [...workReferenceData];
    newArr.splice(newArr.length - 1);
    setWorkReferenceData(newArr);
  };

  const customHtml = (
    <Fragment>
      <div style={{ display: "flex", textAlign: "center", color: "#4d91b3" }}>
        <p style={{ fontWeight: "bold", width: "100%" }}>
          We require 3 years worth of work references from you; you must ensure
          this is the last 3 years up to present day. Please provide details
          below. If you have had more than one job throughout the 3 year period,
          select ‘Add More’ to give further details.
        </p>
      </div>
      {workReferenceData.map((el, i) => (
        <Fragment key={i}>
          <HorizontalLine text="Work Reference" desc={`Referee ${i + 1}`} />
          <form>
            <label className="grey-text">From*</label>
            <input
              type="date"
              value={el.from}
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "from", i)
              }
              required
              className="form-control"
            />
            <label className="grey-text">To*</label>
            <input
              value={el.to}
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "to", i)
              }
              required
              type="date"
              className="form-control"
            />
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage.work[i] &&
                validationErrorMessage.work[i].toAfterFromDate}
            </p>
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage.work[i] &&
                validationErrorMessage.work[i].wrongDate}
            </p>
            <label className="grey-text">Contact Name*</label>
            <input
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "referee", i)
              }
              value={el.referee}
              required
              className="form-control"
            />
            <label className="grey-text">Job Title*</label>
            <input
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "jobTitle", i)
              }
              value={el.jobTitle}
              required
              className="form-control"
            />

            <label className="grey-text">Company*</label>
            <input
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "company", i)
              }
              value={el.company}
              className="form-control"
            />
            <label className="grey-text">
              Email* <small>(must be a business email address)</small>
            </label>
            <input
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "email", i)
              }
              value={el.email}
              required
              className="form-control"
            />
            <label className="grey-text">Contact Number*</label>
            <input
              onChange={(e) =>
                setValue(workReferenceData, e.target.value, "telephone", i)
              }
              value={el.telephone}
              required
              className="form-control"
            />
            <label className="grey-text">
              Can we contact the referee now?*
            </label>
            <OptionsButton
              items={[
                {
                  text: "Yes",
                  key: `${i}`,
                  value: "Yes",
                  isActive: el.canContactReferee === "Yes",
                },
                {
                  text: "No",
                  key: `${i}`,
                  value: "No",
                  isActive: el.canContactReferee === "No",
                },
              ]}
              handleChange={(k, v) => {
                setValue(workReferenceData, v, "canContactReferee", i);
              }}
            />

            <p style={{ color: "red", fontSize: "13px", marginTop: "10px" }}>
              {validationErrorMessage.work[i] &&
                workReferenceData[i].additionalInfo === "" &&
                validationErrorMessage.work[i].tooBigGap}
            </p>
            {validationErrorMessage.work[i] &&
              (validationErrorMessage.work[i].tooBigGap ||
                workReferenceData[i].additionalInfo !== "") && (
                <Fragment>
                  <label className="grey-text">
                    Additional Information*{" "}
                    <small>
                      (e.g. in between roles / looking for work / travelling)
                    </small>
                  </label>
                  <input
                    onChange={(e) =>
                      setValue(
                        workReferenceData,
                        e.target.value,
                        "additionalInfo",
                        i
                      )
                    }
                    value={el.additionalInfo}
                    required
                    className="form-control"
                  />
                </Fragment>
              )}
          </form>
          <br />
        </Fragment>
      ))}
      <div>
        <p
          style={{ color: "orange", fontSize: "14px" }}
          className={`${
            threeYearsErrorMessage.length === 0 ? "d-none" : "d-block"
          }`}
        >
          {threeYearsErrorMessage}
        </p>
        <div style={{ marginTop: "-25px", marginBottom: "20px" }}>
          {
            <button
              disabled={workReferenceData.length === 1}
              onClick={removeLast}
              className="btn btn-secondary my-2"
            >
              Remove Last
            </button>
          }
          <button
            onClick={() =>
              setWorkReferenceData([
                ...workReferenceData,
                {
                  from: "",
                  to: "",
                  referee: "",
                  jobTitle: "",
                  company: "",
                  email: "",
                  telephone: "",
                  type: "workReference",
                  additionalInfo: "",
                  canContactReferee: "",
                },
              ])
            }
            className="btn btn-secondary m-2 "
          >
            {" "}
            Add More{" "}
          </button>
        </div>
      </div>
      <div style={{ display: "flex", textAlign: "center", color: "#4d91b3" }}>
        <p style={{ fontWeight: "bold", width: "100%" }}>
          We require 2 personal references from people of good standing and who
          are not directly related to you. You must have known these people for
          5+ years. Please provide details below.
        </p>
      </div>
      {personalReferenceData.map((el, i) => (
        <Fragment key={i}>
          <HorizontalLine text="Personal Reference" desc={`Referee ${i + 1}`} />
          <form>
            <label className="grey-text">From*</label>
            <input
              type="date"
              value={el.from}
              onChange={(e) =>
                setValue(personalReferenceData, e.target.value, "from", i)
              }
              required
              className="form-control"
            />
            <label className="grey-text">To*</label>
            <input
              value={el.to}
              onChange={(e) =>
                setValue(personalReferenceData, e.target.value, "to", i)
              }
              required
              type="date"
              className="form-control"
            />
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage.personal[i] &&
                validationErrorMessage.personal[i].toAfterFromDate}
            </p>
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage.personal[i] &&
                validationErrorMessage.personal[i].wrongDate}
            </p>
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage.personal[i] &&
                validationErrorMessage.personal[i].tooBigGap}
            </p>
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage.personal[i] &&
                validationErrorMessage.personal[i].yearsRange}
            </p>
            <label className="grey-text">Contact Name*</label>
            <input
              onChange={(e) =>
                setValue(personalReferenceData, e.target.value, "referee", i)
              }
              value={el.referee}
              required
              className="form-control"
            />
            <label className="grey-text">Email*</label>
            <input
              onChange={(e) =>
                setValue(personalReferenceData, e.target.value, "email", i)
              }
              value={el.email}
              required
              className="form-control"
            />
            <label className="grey-text">Contact Number*</label>
            <input
              onChange={(e) =>
                setValue(personalReferenceData, e.target.value, "telephone", i)
              }
              value={el.telephone}
              required
              className="form-control"
            />
            <label className="grey-text">
              Can we contact the referee now?*
            </label>
            <OptionsButton
              items={[
                {
                  text: "Yes",
                  key: `${i}`,
                  value: "Yes",
                  isActive: el.canContactReferee === "Yes",
                },
                {
                  text: "No",
                  key: `${i}`,
                  value: "No",
                  isActive: el.canContactReferee === "No",
                },
              ]}
              handleChange={(k, v) => {
                setValue(personalReferenceData, v, "canContactReferee", i);
              }}
            />
          </form>
          <br />
        </Fragment>
      ))}
    </Fragment>
  );

  return (
    <FormCreator
      formTitle={"NHS Referencing"}
      fields={{}}
      setField={() => {}}
      handleSubmit={() => {}}
      handleCancel={() => history.push("/profile")}
      customHtml={customHtml}
      handleSave={handleSave}
      disableSave={shouldDisable}
      isCustomSaveButton={true}
    />
  );
};

NHSReferences.propTypes = {
  appData: any,
  postNHSReferenceFormFunc: func.isRequired,
};

const mapDispatchToProps = {
  postNHSReferenceFormFunc: postNHSReferenceForm,
};
const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);
export default withRedux(NHSReferences);
